import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Instagram, Loader2, Mail, MapPin, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Kicker } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", phone: "", email: "", company: "", ico: "", message: "" };

const inputCls =
  "w-full rounded-md border border-stone-300 bg-white px-4 py-3 text-sm text-stone-900 placeholder:text-stone-400 transition-colors duration-300 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold/40";

export default function LeadForm() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast.success("Poptávka odeslána. Ozveme se vám co nejdříve.");
    } catch (err) {
      toast.error("Odeslání se nezdařilo. Zkuste to prosím znovu, nebo nám napište na e-mail.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="form" className="relative overflow-hidden border-t border-stone-200 bg-[#FAF7F2] py-24 sm:py-32" data-testid="lead-form-section">
      <div className="spotlight absolute inset-0" aria-hidden="true" />
      <div className="relative mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-2">
        <Reveal>
          <Kicker>Předvedení naživo</Kicker>
          <h2 className="mt-4 font-display text-4xl font-black uppercase leading-[0.95] tracking-tighter text-stone-900 sm:text-5xl md:text-6xl">
            Chci zůstat
            <br />
            <span className="text-[#B08968]">v kontaktu</span>
          </h2>
          <p className="mt-8 max-w-md text-base leading-relaxed text-stone-600 sm:text-lg">
            Nejlepší způsob, jak pražičku poznat, je vidět ji v akci — a ochutnat výsledek.
            Vyplňte formulář a domluvíme osobní předvedení.
          </p>
          <div className="group mt-10 max-w-md overflow-hidden rounded-lg border border-stone-200 shadow-sm">
            <img
              src="/img/serve.jpg"
              alt="Nalévání čerstvě pražené kávy z BeanGo Cube X"
              loading="lazy"
              className="w-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-105"
              data-testid="form-serve-image"
            />
          </div>
          <div className="mt-12 space-y-5">
            <div className="flex items-center gap-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-full border border-gold/30 bg-gold/5">
                <Mail className="h-4 w-4 text-gold" />
              </span>
              <a href="mailto:info@galacoffee.cz" className="text-sm text-stone-700 transition-colors duration-300 hover:text-gold" data-testid="footer-contact-email">
                info@galacoffee.cz
              </a>
            </div>
            <div className="flex items-center gap-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-full border border-gold/30 bg-gold/5">
                <Instagram className="h-4 w-4 text-gold" />
              </span>
              <a href="https://instagram.com/beangocube_cz_sk" target="_blank" rel="noopener noreferrer" className="text-sm text-stone-700 transition-colors duration-300 hover:text-gold" data-testid="contact-instagram">
                @beangocube_cz_sk
              </a>
            </div>
            <div className="flex items-center gap-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-full border border-stone-300 bg-white">
                <Phone className="h-4 w-4 text-stone-500" />
              </span>
              <a href="tel:+420731775208" className="text-sm text-stone-700 transition-colors duration-300 hover:text-gold" data-testid="contact-phone">+420 731 775 208</a>
            </div>
            <div className="flex items-center gap-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-full border border-stone-300 bg-white">
                <MapPin className="h-4 w-4 text-stone-500" />
              </span>
              <span className="text-sm text-stone-700" data-testid="contact-address">Nové sady 25, 602 00 Brno</span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="rounded-lg border border-stone-200 bg-white p-8 shadow-sm sm:p-10">
            {done ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="flex min-h-[420px] flex-col items-center justify-center text-center"
                data-testid="lead-form-success"
              >
                <CheckCircle2 className="h-14 w-14 text-gold" />
                <h3 className="mt-6 font-display text-2xl font-bold text-stone-900">Děkujeme za poptávku!</h3>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-stone-600">
                  Ozveme se vám co nejdříve a domluvíme termín předvedení. Mezitím se můžete
                  těšit na čerstvě praženou kávu.
                </p>
                <button
                  onClick={() => { setForm(EMPTY); setDone(false); }}
                  className="mt-8 rounded-full border border-stone-300 px-6 py-3 font-display text-sm font-bold uppercase tracking-wide text-stone-700 transition-colors duration-300 hover:border-gold/60 hover:text-gold"
                  data-testid="lead-form-reset-button"
                >
                  Odeslat další poptávku
                </button>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="lead-form">
                <div>
                  <label htmlFor="lead-name" className="mb-2 block text-xs font-bold uppercase tracking-[0.15em] text-stone-500">
                    Jméno a příjmení *
                  </label>
                  <input id="lead-name" required value={form.name} onChange={set("name")} placeholder="Jan Novák" className={inputCls} aria-label="Jméno a příjmení" data-testid="lead-form-name-input" />
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="lead-phone" className="mb-2 block text-xs font-bold uppercase tracking-[0.15em] text-stone-500">
                      Telefon *
                    </label>
                    <input id="lead-phone" required type="tel" value={form.phone} onChange={set("phone")} placeholder="+420 777 123 456" className={inputCls} aria-label="Telefon" data-testid="lead-form-phone-input" />
                  </div>
                  <div>
                    <label htmlFor="lead-email" className="mb-2 block text-xs font-bold uppercase tracking-[0.15em] text-stone-500">
                      E-mail *
                    </label>
                    <input id="lead-email" required type="email" value={form.email} onChange={set("email")} placeholder="jan@priklad.cz" className={inputCls} aria-label="E-mail" data-testid="lead-form-email-input" />
                  </div>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="lead-company" className="mb-2 block text-xs font-bold uppercase tracking-[0.15em] text-stone-500">
                      Firma <span className="normal-case text-stone-400">(nepovinné)</span>
                    </label>
                    <input id="lead-company" value={form.company} onChange={set("company")} placeholder="Kavárna s.r.o." className={inputCls} aria-label="Firma" data-testid="lead-form-company-input" />
                  </div>
                  <div>
                    <label htmlFor="lead-ico" className="mb-2 block text-xs font-bold uppercase tracking-[0.15em] text-stone-500">
                      IČO <span className="normal-case text-stone-400">(nepovinné)</span>
                    </label>
                    <input id="lead-ico" value={form.ico} onChange={set("ico")} placeholder="12345678" className={inputCls} aria-label="IČO" data-testid="lead-form-ico-input" />
                  </div>
                </div>
                <div>
                  <label htmlFor="lead-message" className="mb-2 block text-xs font-bold uppercase tracking-[0.15em] text-stone-500">
                    Poznámka <span className="normal-case text-stone-400">(nepovinné)</span>
                  </label>
                  <textarea id="lead-message" rows={3} value={form.message} onChange={set("message")} placeholder="Preferovaný termín, lokalita, dotazy…" className={`${inputCls} resize-none`} aria-label="Poznámka" data-testid="lead-form-message-input" />
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  type="submit"
                  disabled={loading}
                  className="flex w-full items-center justify-center gap-3 rounded-full bg-gold px-8 py-4 font-display text-base font-bold uppercase tracking-wide text-white transition-colors duration-300 hover:bg-gold-light disabled:opacity-60"
                  data-testid="lead-form-submit-button"
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-4 w-4" />}
                  {loading ? "Odesílám…" : "Chci zůstat v kontaktu"}
                </motion.button>
                <p className="text-center text-[11px] leading-relaxed text-stone-400">
                  Odesláním souhlasíte se zpracováním údajů pro účely kontaktování ohledně předvedení.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
